import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetProfile,
  useUpdateProfile,
  useSchoolFreshStart,
  useListTimetables,
  getGetProfileQueryKey,
  getListTimetablesQueryKey,
} from "@workspace/api-client-react";
import type { Profile, SchoolFreshStartResult } from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  Loader2,
  CalendarDays,
  Archive,
  EyeOff,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  GraduationCap,
} from "lucide-react";
import { useDiary } from "@/contexts/DiaryContext";

// Read the user's preferences bag without losing other keys when we write
// our freshStartStatus marker back. Mirrors merge-wizard's pattern.
function profilePreferences(profile: Profile | undefined): Record<string, unknown> {
  const prefs = profile?.preferences;
  return prefs && typeof prefs === "object" ? (prefs as Record<string, unknown>) : {};
}

function todayYmd(): string {
  // Local-time today as YYYY-MM-DD so the date picker default matches what
  // the user sees on their calendar (avoids the "yesterday in UTC" trap).
  const now = new Date();
  const tz = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - tz).toISOString().slice(0, 10);
}

type Step = "intro" | "date" | "confirm" | "success";

export function FreshStartWizard({
  open,
  onClose,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { data: profile } = useGetProfile();
  const { data: timetables = [] } = useListTimetables();
  const updateProfile = useUpdateProfile();
  const freshStart = useSchoolFreshStart();
  const { setActiveTimetableId } = useDiary();

  const [step, setStep] = useState<Step>("intro");
  const [joinDate, setJoinDate] = useState<string>(todayYmd());
  const [result, setResult] = useState<SchoolFreshStartResult | null>(null);
  // Once the user has applied the change, never downgrade their stored
  // status back to "dismissed" if they then close the dialog via ESC,
  // backdrop, or the X button. Without this guard a successful run
  // followed by a casual close would silently overwrite "completed".
  const completedRef = useRef(false);

  // Sensible date-picker bounds. Lower bound matches the server's
  // ~2-year-back allowance so a teacher who skipped fresh-start at
  // sign-up and runs it months (even a year) later can still backdate
  // to their real first day at the school. Upper bound (~18 months)
  // covers the longest realistic Australian "I start next school year"
  // scenario without giving teachers a runway to typo themselves into
  // a 50-year-out diary.
  const dateBounds = useMemo(() => {
    const tzOffsetMs = new Date().getTimezoneOffset() * 60_000;
    const minDate = new Date(Date.now() - 2 * 365 * 24 * 60 * 60 * 1000);
    const maxDate = new Date();
    maxDate.setUTCMonth(maxDate.getUTCMonth() + 18);
    const minYmd = new Date(minDate.getTime() - tzOffsetMs).toISOString().slice(0, 10);
    const maxYmd = new Date(maxDate.getTime() - tzOffsetMs).toISOString().slice(0, 10);
    return { min: minYmd, max: maxYmd };
  }, []);

  // Reset to step 1 each time the dialog is opened so the user always
  // sees the explanation before committing.
  useEffect(() => {
    if (open) {
      setStep("intro");
      setJoinDate(todayYmd());
      setResult(null);
      completedRef.current = false;
    }
  }, [open]);

  // Snapshot of the user's pre-join data so the intro/confirm steps can
  // describe exactly what's about to be archived/hidden.
  const activeTimetable =
    timetables.find((t) => !t.archived) ?? null;
  const schoolName = profile?.schoolName?.trim() || "your school";

  async function persistStatus(status: "completed" | "dismissed") {
    try {
      await updateProfile.mutateAsync({
        data: {
          preferences: {
            ...profilePreferences(profile),
            freshStartStatus: status,
          },
        },
      });
      qc.invalidateQueries({ queryKey: getGetProfileQueryKey() });
    } catch {
      // Non-fatal: the wizard still closes; the gate will just re-evaluate
      // on next mount. We don't toast here because either path already
      // surfaces an outcome to the user.
    }
  }

  async function handleDismiss() {
    // If the user has already completed the apply step, treat any
    // subsequent close as a benign dismissal (no status write) so we
    // don't trample the freshly-stored "completed" marker.
    if (completedRef.current) {
      onClose();
      return;
    }
    await persistStatus("dismissed");
    onClose();
  }

  // ESC / backdrop / X — only persist a dismiss when there's nothing to
  // protect. Success path goes straight to onClose without overwriting.
  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) return;
    if (completedRef.current || step === "success") {
      onClose();
      return;
    }
    void handleDismiss();
  }

  async function handleApply() {
    try {
      const r = await freshStart.mutateAsync({ data: { joinDate } });
      setResult(r);
      setStep("success");
      completedRef.current = true;
      // /school/fresh-start has already written both
      // preferences.freshStartStatus = "completed" AND
      // preferences.freshStartTimetableId in-tx, so we deliberately do
      // NOT call persistStatus("completed") here — a follow-up
      // PUT /profile would patch from the now-stale cached preferences
      // snapshot and could clobber the freshly-written timetable id
      // marker that the idempotency lookup relies on. Invalidate the
      // profile cache so the next read reflects the server state.
      qc.invalidateQueries({ queryKey: getGetProfileQueryKey() });
      qc.invalidateQueries({ queryKey: getListTimetablesQueryKey() });
      qc.invalidateQueries();
      toast({
        title: r.reused ? "Fresh-start reused" : "Fresh-start complete",
        description: `New diary starts ${joinDate}.`,
      });
      onCompleted?.();
    } catch (err: unknown) {
      toast({
        title: "Couldn't start fresh",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  function handleFinish() {
    if (result?.newTimetableId) {
      setActiveTimetableId(result.newTimetableId);
      navigate("/app/timetable-settings");
    }
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialog-fresh-start">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-primary" />
            Start fresh with your school account
          </DialogTitle>
          <DialogDescription>
            Archive your previous individual diary and set up a clean slate for
            your new school year.
          </DialogDescription>
        </DialogHeader>

        {step === "intro" && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-700">
              You've joined{" "}
              <span className="font-medium text-gray-900">{schoolName}</span>.
              We can tidy up your existing individual account data so it does
              not get mixed in with your new school setup.
            </p>
            <ul className="space-y-3">
              <li className="flex gap-3 text-sm">
                <Archive className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">
                  Archive your current diary{" "}
                  {activeTimetable ? (
                    <span className="text-gray-500">
                      ("{activeTimetable.name}")
                    </span>
                  ) : null}{" "}
                  so its lessons are kept for reference but no longer active.
                </span>
              </li>
              <li className="flex gap-3 text-sm">
                <EyeOff className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">
                  Hide your previous private classes and subjects from menus.
                  Nothing is deleted, and you can unhide them later from
                  Classes or Subjects.
                </span>
              </li>
              <li className="flex gap-3 text-sm">
                <CalendarDays className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">
                  Create a new diary for your school year so you can build
                  your timetable around school-linked classes.
                </span>
              </li>
            </ul>
            <p className="text-xs text-gray-500">
              You can re-run this later from Settings if you would rather skip
              for now.
            </p>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="ghost"
                onClick={handleDismiss}
                disabled={updateProfile.isPending}
                data-testid="button-fresh-start-skip"
              >
                Skip for now
              </Button>
              <Button
                onClick={() => setStep("date")}
                data-testid="button-fresh-start-next-date"
              >
                Continue <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "date" && (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="freshStartJoinDate" className="text-sm font-medium text-gray-700">
                First day at the new school
              </Label>
              <Input
                id="freshStartJoinDate"
                type="date"
                value={joinDate}
                min={dateBounds.min}
                max={dateBounds.max}
                onChange={(e) => setJoinDate(e.target.value)}
                data-testid="input-fresh-start-join-date"
              />
              <p className="text-xs text-gray-500">
                Your new diary will start on this date. Your previous diary
                will end the day before.
              </p>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="ghost"
                onClick={() => setStep("intro")}
                data-testid="button-fresh-start-back-intro"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                onClick={() => setStep("confirm")}
                disabled={
                  !/^\d{4}-\d{2}-\d{2}$/.test(joinDate) ||
                  joinDate < dateBounds.min ||
                  joinDate > dateBounds.max
                }
                data-testid="button-fresh-start-next-confirm"
              >
                Review changes <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2 text-sm">
              <p className="text-gray-700">
                On{" "}
                <span className="font-medium text-gray-900">{joinDate}</span>{" "}
                we'll:
              </p>
              <ul className="space-y-1.5 text-gray-700">
                {activeTimetable && (
                  <li className="flex gap-2">
                    <Archive className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                    Archive "{activeTimetable.name}" and end it the day before.
                  </li>
                )}
                <li className="flex gap-2">
                  <EyeOff className="h-3.5 w-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                  Hide every private class and subject you own.
                </li>
                <li className="flex gap-2">
                  <CalendarDays className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                  Create a fresh diary starting {joinDate}.
                </li>
              </ul>
            </div>
            <p className="text-xs text-gray-500">
              Nothing is permanently deleted. Archived diaries stay readable in
              the Diaries page, and hidden classes/subjects can be unhidden any
              time.
            </p>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="ghost"
                onClick={() => setStep("date")}
                disabled={freshStart.isPending}
                data-testid="button-fresh-start-back-date"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                onClick={handleApply}
                disabled={freshStart.isPending}
                data-testid="button-fresh-start-apply"
              >
                {freshStart.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Start fresh
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "success" && result && (
          <div className="space-y-4 py-2">
            <div className="flex flex-col items-center text-center py-2">
              <CheckCircle2 className="h-10 w-10 text-green-500 mb-2" />
              <p className="text-sm font-semibold text-gray-900">
                {result.reused ? "Picked up where you left off" : "All tidied up"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Your new diary is ready. Build out your weekly timetable next.
              </p>
            </div>
            <ul className="space-y-1 text-xs text-gray-600">
              {result.archivedTimetableId !== null && result.archivedTimetableId !== undefined && (
                <li>· Archived your previous diary.</li>
              )}
              <li>
                · Hidden {result.hiddenClassCount} class
                {result.hiddenClassCount === 1 ? "" : "es"} and{" "}
                {result.hiddenCourseCount} course
                {result.hiddenCourseCount === 1 ? "" : "s"}.
              </li>
              <li>
                · {result.reused ? "Reused" : "Created"} a fresh diary starting {joinDate}.
              </li>
            </ul>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="button-fresh-start-done"
              >
                Done
              </Button>
              <Button
                onClick={handleFinish}
                data-testid="button-fresh-start-build-timetable"
              >
                Build my new timetable <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
